import { useContext, type ReactNode } from 'react';
import clsx from 'clsx';
import {
  LikeDislike,
  LikeDislikeVariant,
  LikeDislikeSize,
  LikeDislikeTags,
  type LikeDislikeTagsRatings,
} from '@hungryroot/ratings';
import { type BaseTag } from '@hungryroot/rest';
import {
  useApiV1TagGroupShuffled as useTagGroupShuffled,
  type TagGroups,
} from '@hungryroot/tags';
import { type ProteinTagValue } from '../../defs/quiz';
import { QuizContext } from '../../quiz-context';
import { type QuizBinaryTagKeys } from '../../quiz-hooks';
import styles from './ControlTagBinaryRating.module.css';

function updateBinaryTags(
  updateProteinTag: ProteinTagValue,
  array: ProteinTagValue[]
): ProteinTagValue[] {
  const { id: updateProteinTagId } = updateProteinTag;
  const updated = array.reduce(
    (seed, binaryTag) => {
      const { id } = binaryTag;
      if (id === updateProteinTagId) {
        return {
          hasMatch: true,
          items: [...seed.items, updateProteinTag],
        };
      }
      return {
        hasMatch: seed.hasMatch,
        items: [...seed.items, binaryTag],
      };
    },
    {
      hasMatch: false,
      items: [],
    } as {
      hasMatch: boolean;
      items: ProteinTagValue[];
    }
  );

  const { hasMatch, items } = updated;
  if (hasMatch === true) {
    return items;
  }

  return [...array, updateProteinTag];
}

function getTagRating(
  tagId: number,
  array: ProteinTagValue[]
): LikeDislikeTagsRatings {
  const match = array.find(({ id }) => {
    return id === tagId;
  });

  if (match === undefined) {
    return LikeDislikeTags.NoRating;
  }

  return match.liked;
}

interface Props<K extends QuizBinaryTagKeys> {
  testid: string;
  tagGroupId: TagGroups;
  question: K;
  filter?: (tag: BaseTag) => boolean;
  children: (params: {
    tag: BaseTag;
    likeDislike: ReactNode;
    index: number;
  }) => ReactNode;
  layoutVariant?: 'one-column' | 'two-column';
}

export function ControlTagBinaryRating<K extends QuizBinaryTagKeys>(
  props: Props<K>
) {
  const { testid, tagGroupId, question, children, layoutVariant, filter } =
    props;

  const tagGroup = useTagGroupShuffled({ tagGroupId });
  const { data } = tagGroup;
  const { quiz, onQuestionAnswer } = useContext(QuizContext);

  if (quiz === null) {
    return null;
  }

  const { [question]: value } = quiz;
  const normalized: ProteinTagValue[] = value === null ? [] : value;

  if (data === undefined) {
    return null;
  }

  return (
    <div
      data-testid={testid}
      key="control-tag-binary-ratings"
      className={clsx({
        [styles['one-column']]: layoutVariant === 'one-column',
        [styles['two-column']]: layoutVariant === 'two-column',
      })}
    >
      {data.tags[0].filter(filter ?? (() => true)).map((tag, index) => {
        const { id: tagId } = tag;
        const tagCyid = `${testid}-toggle-tag-${tagId}`;
        const likeDislike = (
          <LikeDislike
            key={tagId}
            testid={`${tagCyid}-binary-rating`}
            size={LikeDislikeSize.Small}
            rating={getTagRating(tagId, normalized)}
            onRating={(value) => {
              const nextCuisines = updateBinaryTags(
                {
                  id: tagId,
                  liked: value as LikeDislikeTagsRatings,
                },
                normalized
              );

              onQuestionAnswer(quiz, question, nextCuisines);
            }}
            variant={LikeDislikeVariant.Tag}
          />
        );
        return children({
          tag,
          likeDislike,
          index,
        });
      })}
    </div>
  );
}
